import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import type { RuntimeState } from '../../atoms/state';
import { Button } from '@/components/ui/button';
import type { AspectRatio } from '../../constants/options';

type VideoPreviewProps = {
  runtime: RuntimeState;
  onRetry: () => void;
  aspectRatio?: AspectRatio;
};

const ratioMap: Record<AspectRatio, number> = {
  '16:9': 16 / 9,
  '9:16': 9 / 16,
  '4:5': 4 / 5,
};

let lastToastedTaskId: string | undefined;

const VideoPreview = ({ runtime, onRetry, aspectRatio = '16:9' }: VideoPreviewProps) => {
  const { t } = useTranslation();
  const ratio = ratioMap[aspectRatio] ?? ratioMap['16:9'];

  const [progress, setProgress] = useState(0);
  const [showGeneratingOverlay, setShowGeneratingOverlay] = useState(false);

  const progressIntervalRef = useRef<number | null>(null);
  const finishRafRef = useRef<number | null>(null);
  const finishTimeoutRef = useRef<number | null>(null);
  const latestProgressRef = useRef(0);

  useEffect(() => {
    latestProgressRef.current = progress;
  }, [progress]);

  const clearProgressInterval = useCallback(() => {
    if (progressIntervalRef.current !== null) {
      window.clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const clearFinishAnimation = useCallback(() => {
    if (finishRafRef.current !== null) {
      window.cancelAnimationFrame(finishRafRef.current);
      finishRafRef.current = null;
    }
    if (finishTimeoutRef.current !== null) {
      window.clearTimeout(finishTimeoutRef.current);
      finishTimeoutRef.current = null;
    }
  }, []);

  // Time-based pseudo progress while generating, capped below 100%.
  useEffect(() => {
    clearProgressInterval();

    if (runtime.status !== 'generating') {
      return;
    }

    setProgress(0);

    setShowGeneratingOverlay(true);

    const start = performance.now();

    const tick = () => {
      const elapsedMs = performance.now() - start;
      const expectedMs = 90_000; // ~90s baseline
      const ratio = Math.min(1, elapsedMs / expectedMs);

      // Dynamic cap moves from ~70% up to ~94% to leave headroom for finish animation.
      const earlyCap = 70;
      const lateCap = 94;
      const dynamicCap = earlyCap + (lateCap - earlyCap) * ratio;

      const ideal = 5 + (dynamicCap - 5) * ratio;

      setProgress((prev) => {
        const safePrev = Number.isFinite(prev) ? prev : 0;
        const target = Math.max(safePrev, ideal);

        // Smooth step: at most ~0.4% per tick (~0.8%/s at 500ms)
        const maxStep = 0.4;
        const delta = target - safePrev;
        if (delta <= 0.01) return safePrev;
        const step = Math.min(delta, maxStep);
        return safePrev + step;
      });
    };

    tick();
    progressIntervalRef.current = window.setInterval(tick, 500);

    return () => {
      clearProgressInterval();
    };
  }, [clearProgressInterval, runtime.status]);

  // Smoothly finish to 100% when backend marks status as ready.
  useEffect(() => {
    clearFinishAnimation();

    if (runtime.status !== 'ready') return;

    clearProgressInterval();
    setShowGeneratingOverlay(true);

    const from = Math.min(99, Math.max(10, latestProgressRef.current));
    const remaining = 100 - from;
    const baseDuration = 700;
    const extra = Math.min(1200, remaining * 30);
    const duration = Math.max(900, baseDuration + extra);

    const start = performance.now();

    const step = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = from + (100 - from) * t;
      setProgress(eased);

      if (t < 1) {
        finishRafRef.current = window.requestAnimationFrame(step);
      } else {
        finishRafRef.current = null;
        finishTimeoutRef.current = window.setTimeout(() => {
          setShowGeneratingOverlay(false);
        }, 250);
      }
    };

    finishRafRef.current = window.requestAnimationFrame(step);

    return () => {
      clearFinishAnimation();
    };
  }, [clearFinishAnimation, clearProgressInterval, runtime.status]);

  useEffect(() => {
    if (runtime.status === 'generating' || runtime.status === 'ready') return;
    clearProgressInterval();
    clearFinishAnimation();
    setShowGeneratingOverlay(false);
    setProgress(0);
  }, [clearFinishAnimation, clearProgressInterval, runtime.status]);

  useEffect(() => {
    const completedTaskId = runtime.lastCompletedTaskId;
    if (
      runtime.status === 'ready' &&
      runtime.previewUrl &&
      completedTaskId &&
      completedTaskId !== lastToastedTaskId
    ) {
      toast.success(t('image-to-video-success'));
      lastToastedTaskId = completedTaskId;
    }
  }, [runtime.lastCompletedTaskId, runtime.previewUrl, runtime.status, t]);

  if (runtime.status === 'generating' || showGeneratingOverlay) {
    return (
      <div className="flex w-full flex-col items-center gap-3 text-sm text-gray-700">
        <div
          className="mx-auto flex w-full max-w-[1004px] flex-col items-center justify-center rounded-[5px] border border-dashed border-primary-light bg-primary-light/5 px-6 py-10"
          style={{ aspectRatio: ratio }}
        >
          <div className="mb-4 text-base font-semibold text-text-primary">
            {t('image-to-video-generating')}
          </div>
          <div className="h-2 w-full max-w-[480px] overflow-hidden rounded-full bg-primary-light/20">
            <div
              className="h-full rounded-full bg-primary-light transition-[width] duration-300"
              style={{ width: `${Math.round(progress)}%` }}
            />
          </div>
          <div className="mt-2 text-xs text-gray-600">{Math.round(progress)}%</div>
        </div>
      </div>
    );
  }

  if (runtime.status === 'error') {
    return (
      <div className="flex w-full flex-col items-center gap-3 text-sm text-red-600">
        <div>{runtime.errorMessage || t('image-to-video-error')}</div>
        <Button size="sm" variant="outline" onClick={onRetry}>
          {t('retry')}
        </Button>
      </div>
    );
  }

  if (runtime.status === 'ready' && runtime.previewUrl) {
    return (
      <div className="flex w-full flex-col items-center gap-3">
        <div
          className="mx-auto w-full max-w-[1004px] overflow-hidden rounded-[5px] border bg-gray-50"
          style={{ aspectRatio: ratio }}
        >
          <video
            src={runtime.previewUrl}
            controls
            className="h-full w-full rounded-[5px] bg-gray-50 object-contain"
          >
            <track kind="captions" src="" srcLang="en" label="Captions" />
          </video>
        </div>
        <div className="text-xs text-gray-500">{t('image-to-video-preview-ready')}</div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-center gap-2 text-sm text-gray-500">
      <div
        className="mx-auto flex w-full max-w-[1004px] items-center justify-center rounded-[5px] border border-dashed border-gray-300 bg-gray-50 px-6 text-center"
        style={{ aspectRatio: ratio }}
      >
        <div className="text-xs text-gray-500">{t('image-to-video-empty')}</div>
      </div>
    </div>
  );
};

export default VideoPreview;
